import { AssignmentListItem } from 'api/gql-utils/flexibleBenefits/types';
import { BenefitsUnitTypes } from 'components/SalaryComponents/views/FlexibleBenefits/types';
import { AppError } from 'utils/AppError';

export type SummaryCardData = {
  annualCtc: number;
  totalFixedValue: number;
  totalVariableValue: number;
  totalOtherCtcValue: number;
  fixedPercentage: number;
  variablePercentage: number;
  otherCtcPercentage: number;
  totalRecurringDeductionAmount: number;
};

export type VpfData = {
  vpfAmount: number;
  maximumAllowedAmount: number;
};

export type CtcBreakdownData = {
  requestId?: string;
  hasStructureChange?: boolean;
  fixed: {
    data: {
      id: string;
      componentName: string;
      perMonthAmount: number;
      perYearAmount: number;
    }[];
    totalMonthlyAmount: number;
    totalYearlyAmount: number;
  };
  variable: {
    data: {
      id: string;
      componentName: string;
      perYearAmount: number;
    }[];
    totalYearlyAmount: number;
  };
  otherCtc: {
    data: {
      id: string;
      componentName: string;
      perMonthAmount?: number;
      perYearAmount: number;
    }[];
    totalMonthlyAmount?: number;
    totalYearlyAmount: number;
  };
  arrears?: {
    data: {
      id: string;
      componentName: string;
      perMonthAmount?: number;
      perYearAmount: number;
    }[];
    totalMonthlyAmount?: number;
    totalYearlyAmount: number;
    arrearSources?: string[];
  };
};

export type RecurringDeductionsFormData = {
  componentId: string;
  monthlyAmount?: number;
};

export type DeductionData = {
  componentId: string;
  componentName?: string;
  monthlyAmount?: number;
};

export type RecurringDeductionsData = DeductionData & {
  id: string;
};

export interface PerquisitesFormData {
  componentId?: string;
  amountPaidByEmployee?: number;
  totalValue?: number;
  id?: string;
}

export type PerquisiteData = {
  id: string;
  component: string;
  amountPaidByEmployee: number;
  totalValue: number;
  componentId: string;
};

export type MonthlyDeclaration =
  | {
      id: string;
      type: 'FIXED' | 'RANGE';
      value: string;
      suffix: string;
    }
  | {
      id: string;
      type: 'LIST_OF_OPTION';
      options: {
        value: string;
        label: string;
      }[];
      value: string;
      suffix: string;
    };

export type BenefitTierOption = {
  id: string;
  label: string;
  max: number;
};

export type AssignedFlexibleComponentsTableDataType = {
  id: string;
  display_name: string;
  component_name: string;
  maxValuePerMonth: string;
  monthlyDeclaration: MonthlyDeclaration;
  assignment_id?: string;
  unit?: BenefitsUnitTypes;
  canDeclare: boolean;
  taxableType?: string;
  tiers?: BenefitTierOption[];
  selectedTierId?: string;
};

export interface MetaInfo {
  userId: string | number | undefined;
  getValue: (id: string) => string;
  getTierId?: (id: string) => string | undefined;
  now: string;
}

export type AssignmentListItemWithId = AssignmentListItem & {
  id: string;
};

export type UseAssignedComponentsReturn = {
  assignedFlexibleComponents: AssignmentListItemWithId[] | undefined;

  errorAssignedComponents: AppError | null;

  flexibleComponentsData?: AssignedFlexibleComponentsTableDataType[];

  allFlexibleComponentsData?: AssignedFlexibleComponentsTableDataType[];

  totalAssigned: number;
  totalAllocated: number;
  totalUnallocated: number;

  monthlyDeclarations: Record<string, string>;
  onMonthlyDeclarationChange: (params: { id: string; value: string }) => void;
  monthlyDeclarationSnapShot: Record<string, string>;
  monthlyDeclarationErrors: Record<string, string>;
  setMonthlyDeclarationErrors: (val: Record<string, string>) => void;

  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  selectedIdSnapShot: string[];

  invalidateQueries: () => Promise<void>;
  refetchData: () => Promise<void>;

  hasError: boolean;
  isFetching: boolean;
  isLoading: boolean;
};

export const DECLARATION_WINDOW_TYPES = {
  ALWAYS_OPEN: 'always_open',
  CUSTOM_RANGE: 'custom_range',
  MONTHLY_RANGE: 'monthly_range',
} as const;

export type DeclarationWindowType = (typeof DECLARATION_WINDOW_TYPES)[keyof typeof DECLARATION_WINDOW_TYPES];

export interface DateRangeValue {
  start: number;
  end: number;
}

export interface NumberRangeValue {
  value?: {
    start?: number;
    end?: number;
  };
}

export interface DeclarationWindowSetting {
  boolean_value?: { value?: boolean };
  date_ranges_value?: { values?: DateRangeValue[] };
  number_range_value?: NumberRangeValue;
}

export interface DeclarationWindowConfig {
  active_key?: DeclarationWindowType | string;
  values?: Record<string, DeclarationWindowSetting>;
}